/**
 * Where the agentic workflow editor goes when you press Back.
 *
 * The editor is opened from several places — a project, a campaign, the agent
 * marketplace, the intelligence hub, a task flow, and from itself when you drill
 * into a sub-flow — so Back has no single destination. It used to call
 * navigate(-1) and step one entry through browser history, which breaks as soon
 * as anything else pushes an entry:
 *
 *   [project] -> [customize] -> [backtest dashboard] -> [customize]
 *
 * Returning from the dashboard pushes a SECOND customize entry, so one step back
 * lands on the dashboard. Every round trip buries the origin one entry deeper.
 *
 * Callers now say where they came from in `returnTo`, and these resolve it.
 */

/**
 * The page Back should go to, or null to fall back to browser history.
 *
 * Null is the honest answer for an editor opened by a pasted link or by a caller
 * that predates `returnTo` — there is nothing recorded to return to, and history
 * is still better than guessing. The destination is deliberately NOT derived
 * from campaign_id / project_id: both are carried for the Run button, and a
 * visitor from the project page and one from the campaign page arrive with an
 * identical pair.
 */
export function backTarget(locationState) {
    const returnTo = locationState && locationState.returnTo;
    if (!returnTo || typeof returnTo.pathname !== 'string' || !returnTo.pathname) return null;
    return { pathname: returnTo.pathname, state: returnTo.state };
}

/**
 * A `returnTo` describing the editor as it stands, for a page it is about to open.
 *
 * Carries the editor's own `returnTo` forward, so the chain unwinds one level at
 * a time: a sub-flow returns to its parent flow, and that parent still returns
 * to the project or marketplace it came from.
 *
 * Every page the editor opens gets this, rather than the editor's own origin.
 * Handing on the origin would make that page's Back skip the editor entirely and
 * jump two levels — which is what the backtest dashboard used to do, except it
 * hardcoded a return to the editor and so ignored its `returnTo` altogether.
 */
export function returnToCurrentFlow({ flowTemplateCode, locationState = {}, campaignId, projectId }) {
    return {
        pathname: '/AIStudio/customize',
        // What Back should say when this is the destination. Pages the editor
        // opens can be reached from elsewhere too, so "Back to workflow" is not
        // theirs to assume.
        label: 'Back to workflow',
        state: {
            flow_template_code: flowTemplateCode || locationState.flow_template_code,
            operator_name: locationState.operator_name,
            operator_id: locationState.operator_id,
            fromOperator: locationState.fromOperator,
            campaign_id: campaignId,
            project_id: projectId,
            returnTo: locationState.returnTo,
        },
    };
}
